import React, { useState, useCallback, useContext, useEffect } from "react";
import Hero from "../home/Hero";
import FilterSidebar from "../home/FilterSidebar";
import ResultsSection from "../home/ResultsSection";
import RepoResultsSection from "../home/RepoResultsSection";
import InsightsPanel from "../home/InsightsPanel";
import FeaturedDevelopers from "../home/FeaturedDevelopers";
import CTASection from "../home/CTASection";
import SeoContentSection from "../home/SeoContentSection";
import SidebarAccordion from "../layout/SidebarAccordion";
import GithubContext from "../../context/github/githubContext";
import { defaultFilterState } from "../home/filterDefaults";

const Home = () => {
  const {
    searchUsers,
    searchRepositories,
    users,
    repoResults,
    searchLoading,
    repoSearchLoading,
    apiError,
    clearApiError,
    resetHomeSearch,
    insightLogin,
    insightByLogin,
    buildUserSearchQuery,
  } = useContext(GithubContext);

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [searchMode, setSearchMode] = useState("developers");
  const [filters, setFilters] = useState(defaultFilterState);
  const [lastQuery, setLastQuery] = useState({
    text: "",
    filters: defaultFilterState,
    mode: "developers",
  });

  useEffect(() => {
    document.title = "GitHub Finder | Search GitHub Developers & Repositories";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Discover GitHub developers and public repositories in one app. Filter by language, skills, and activity. Profile and repo cards from the GitHub API."
      );
    }
  }, []);

  useEffect(() => {
    if (!mobileFiltersOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMobileFiltersOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileFiltersOpen]);

  useEffect(() => {
    if (!mobileFiltersOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileFiltersOpen]);

  const runSearch = useCallback(
    (text, filterSnapshot, modeArg) => {
      const mode = modeArg ?? searchMode;
      const t = (text || "").trim();
      const f = filterSnapshot ? { ...filterSnapshot } : { ...filters };
      setLastQuery({ text: t, filters: f, mode });
      if (mode === "repositories") {
        searchRepositories(t, f, 1);
      } else {
        const q = buildUserSearchQuery(t, f);
        if (!q || q === "type:user") {
          return;
        }
        searchUsers(t, f, 1);
      }
    },
    [filters, searchMode, searchUsers, searchRepositories, buildUserSearchQuery]
  );

  const handleHeroSubmit = (text) => {
    clearApiError();
    runSearch(text, filters, searchMode);
  };

  /** When the query is cleared (clear button or empty input), restore default home (featured, no stale results). */
  const handleSearchQueryChange = useCallback(
    (value) => {
      const t = (value || "").trim();
      if (t !== "") return;
      if (!lastQuery.text?.trim()) return;
      setLastQuery({
        text: "",
        filters: { ...defaultFilterState },
        mode: searchMode,
      });
      setFilters({ ...defaultFilterState });
      resetHomeSearch();
    },
    [lastQuery.text, searchMode, resetHomeSearch]
  );

  const handleApplyFilters = () => {
    clearApiError();
    const f = { ...filters };
    setLastQuery((p) => ({
      ...p,
      text: lastQuery.text,
      filters: f,
      mode: searchMode,
    }));
    if (searchMode === "repositories") {
      searchRepositories(lastQuery.text, f, 1);
      return;
    }
    const q = buildUserSearchQuery(lastQuery.text, f);
    if (!q || q === "type:user") {
      return;
    }
    searchUsers(lastQuery.text, f, 1);
  };

  const handleClearFilters = () => {
    setFilters({ ...defaultFilterState });
    if (!lastQuery.text?.trim()) return;
    clearApiError();
    setLastQuery((p) => ({
      ...p,
      filters: defaultFilterState,
      mode: searchMode,
    }));
    if (searchMode === "repositories") {
      searchRepositories(lastQuery.text, defaultFilterState, 1);
    } else {
      const q = buildUserSearchQuery(lastQuery.text, defaultFilterState);
      if (q && q !== "type:user") {
        searchUsers(lastQuery.text, defaultFilterState, 1);
      }
    }
  };

  const handleSearchModeChange = (mode) => {
    if (mode === searchMode) return;
    setMobileFiltersOpen(false);
    setSearchMode(mode);

    const t = (lastQuery.text || "").trim();
    if (!t) {
      setLastQuery((p) => ({ ...p, mode }));
      return;
    }

    clearApiError();
    setLastQuery((p) => ({
      ...p,
      mode,
      text: t,
      filters: { ...filters },
    }));

    if (mode === "repositories") {
      searchRepositories(t, filters, 1);
      return;
    }
    const q = buildUserSearchQuery(t, filters);
    if (!q || q === "type:user") {
      return;
    }
    searchUsers(t, filters, 1);
  };

  const handleSortDevelopers = (sortUsers) => {
    const f = { ...filters, sortUsers };
    setFilters(f);
    if (!lastQuery.text?.trim()) return;
    clearApiError();
    const q = buildUserSearchQuery(lastQuery.text, f);
    if (!q || q === "type:user") return;
    setLastQuery((p) => ({ ...p, filters: f, mode: "developers" }));
    searchUsers(lastQuery.text, f, 1);
  };

  const handleSortRepositories = (sortRepo) => {
    const f = { ...filters, sortRepo };
    setFilters(f);
    if (!lastQuery.text?.trim()) return;
    clearApiError();
    setLastQuery((p) => ({ ...p, filters: f, mode: "repositories" }));
    searchRepositories(lastQuery.text, f, 1);
  };

  const handleUserPage = (p) => {
    searchUsers(lastQuery.text, lastQuery.filters, p);
  };

  const handleRepoPage = (p) => {
    searchRepositories(lastQuery.text, lastQuery.filters, p);
  };

  const scrollToSearch = () => {
    const el = document.getElementById("gf-search-anchor");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
    const input = el?.querySelector?.("input[type='search'], input[name='q']");
    input?.focus?.();
  };

  const loadingAny = searchLoading || repoSearchLoading;
  const hasUserResults = users.length > 0;
  const hasRepoResults = repoResults.length > 0;
  const showFeatured =
    !hasUserResults && !hasRepoResults && !loadingAny && !lastQuery.text;

  const insightData = insightLogin ? insightByLogin[insightLogin] : null;

  const showEmpty =
    !loadingAny &&
    lastQuery.text &&
    searchMode === "developers" &&
    !hasUserResults;
  const showEmptyRepo =
    !loadingAny &&
    lastQuery.text &&
    searchMode === "repositories" &&
    !hasRepoResults;

  const filterMode =
    searchMode === "repositories" ? "repositories" : "developers";

  const sidebarFilters = (
    <FilterSidebar
      mode={filterMode}
      filters={filters}
      onChange={setFilters}
      onApply={handleApplyFilters}
      onClear={handleClearFilters}
      hideTitle
    />
  );

  const insightsTitle =
    insightLogin != null && insightLogin !== ""
      ? `Insights · @${insightLogin}`
      : "Insights";

  const sidebarFiltersDrawer = (
    <FilterSidebar
      mode={filterMode}
      filters={filters}
      onChange={setFilters}
      onApply={() => {
        handleApplyFilters();
        setMobileFiltersOpen(false);
      }}
      onClear={() => {
        handleClearFilters();
        setMobileFiltersOpen(false);
      }}
      hideTitle
    />
  );

  return (
    <div className="gf-page gf-tool-page">
      <div className="gf-hero-band gf-hero-band--home">
        <div className="gf-container">
          <Hero
            initialText={lastQuery.text}
            onSubmit={handleHeroSubmit}
            onQueryChange={handleSearchQueryChange}
            searchMode={searchMode}
            onSearchModeChange={handleSearchModeChange}
          />
        </div>
      </div>

      <div className="gf-container gf-tool-page-inner">
        <div className="gf-tool-shell">
          <aside
            className="gf-filter-sidebar gf-filter-sidebar--layout"
            aria-label="Insights and search filters"
          >
            {searchMode === "developers" && (
              <SidebarAccordion title={insightsTitle} defaultOpen>
                <div className="gf-sidebar-insights">
                  <InsightsPanel
                    compact
                    omitHeading
                    login={insightLogin}
                    data={insightData}
                  />
                </div>
              </SidebarAccordion>
            )}
            <SidebarAccordion title="Filters" defaultOpen>
              {sidebarFilters}
            </SidebarAccordion>
          </aside>

          <div className="gf-tool-main">
          {apiError && (
            <div className="gf-banner gf-banner--error" role="alert">
              <div className="gf-banner__text">
                <strong className="gf-banner__title">
                  {typeof apiError === "string" ? apiError : apiError.title}
                </strong>
                {typeof apiError === "object" && apiError.detail ? (
                  <p className="gf-banner__detail">{apiError.detail}</p>
                ) : null}
              </div>
              <button type="button" className="gf-btn gf-btn--ghost gf-btn--sm" onClick={clearApiError}>
                Dismiss
              </button>
            </div>
          )}

          {mobileFiltersOpen && (
            <div
              className="gf-drawer-overlay gf-drawer-overlay--left"
              role="presentation"
              onClick={() => setMobileFiltersOpen(false)}
            >
              <aside
                id="gf-mobile-filters-drawer"
                className="gf-filter-drawer"
                role="dialog"
                aria-modal="true"
                aria-labelledby="gf-mobile-filters-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="gf-filter-drawer__head">
                  <h2 id="gf-mobile-filters-title" className="gf-filter-drawer__title">
                    Advanced filters
                  </h2>
                  <button
                    type="button"
                    className="gf-btn gf-btn--ghost gf-btn--sm"
                    onClick={() => setMobileFiltersOpen(false)}
                    aria-label="Close filters"
                  >
                    ✕
                  </button>
                </div>
                <div className="gf-filter-drawer__body">
                  {searchMode === "developers" && (
                    <SidebarAccordion
                      className="gf-sidebar-accordion--drawer"
                      title={insightsTitle}
                      defaultOpen
                    >
                      <div className="gf-sidebar-insights gf-sidebar-insights--drawer">
                        <InsightsPanel
                          compact
                          omitHeading
                          login={insightLogin}
                          data={insightData}
                        />
                      </div>
                    </SidebarAccordion>
                  )}
                  <SidebarAccordion
                    className="gf-sidebar-accordion--drawer"
                    title="Filters"
                    defaultOpen
                  >
                    {sidebarFiltersDrawer}
                  </SidebarAccordion>
                </div>
              </aside>
            </div>
          )}

          <div className="gf-results-layout">
            <div className="gf-results-layout__primary">
              {showFeatured && <FeaturedDevelopers />}

              {searchMode === "developers" && (
                <ResultsSection
                  keyword={lastQuery.text}
                  onPageChange={handleUserPage}
                  sortValue={filters.sortUsers}
                  onSortChange={handleSortDevelopers}
                />
              )}

              {searchMode === "repositories" && (
                <RepoResultsSection
                  keyword={lastQuery.text}
                  onPageChange={handleRepoPage}
                  sortValue={filters.sortRepo}
                  onSortChange={handleSortRepositories}
                />
              )}

              {showEmpty && (
                <p className="gf-empty gf-empty--card" role="status">
                  No results found. Try different filters or a broader keyword.
                </p>
              )}
              {showEmptyRepo && (
                <p className="gf-empty gf-empty--card" role="status">
                  No repositories found. Try different filters or a broader keyword.
                </p>
              )}
            </div>
          </div>

            <SeoContentSection />
            <CTASection onSearchClick={scrollToSearch} />
          </div>
        </div>

      </div>

      <button
        type="button"
        className={`gf-home-filters-fab${mobileFiltersOpen ? " is-hidden" : ""}`}
        onClick={() => setMobileFiltersOpen(true)}
        aria-controls="gf-mobile-filters-drawer"
        aria-label="Open advanced filters"
      >
        <span className="gf-user-profile-fab__ico" aria-hidden="true">
          ☰
        </span>
        <span className="gf-user-profile-fab__txt">Filters</span>
      </button>
    </div>
  );
};

export default Home;
